<!--
  @component BillingPanel — content of the Billing screen.
  tier:      screen content (lives inside GatesIO mount provided
             by BillingScreen)
  reads:     useIdentityIO          → user + projects
             useGatesIO             → verdicts + lastError
             useLiveTimelineEdge    → gate.* projection
             useIO                  → effecting? → FaceLedgerNotice
-->
<script lang="ts">
  import { useIdentityIO } from "./IdentityIO.svelte";
  import { useGatesIO } from "../../../global/io/nodes/deployment/GatesIO.svelte";
  import { useDeploymentIO } from "../../../global/io/nodes/deployment/DeploymentIO.svelte";
  import { useLiveTimelineEdge } from "../../../global/io/edges/deployment/LiveTimelineEdge.svelte";
  import { useIO } from "../../../global/io/IO.svelte";

  import QuotaMeter from "../../../global/visuals/primitives/QuotaMeter.svelte";
  import StatusPill from "../../../global/visuals/primitives/StatusPill.svelte";
  import FaceLedgerNotice from "../../../global/visuals/primitives/FaceLedgerNotice.svelte";

  const identity = useIdentityIO();
  const gates = useGatesIO();
  const node = useDeploymentIO();
  const timeline = useLiveTimelineEdge();
  const io = useIO();

  // ── D9: real billing.status ────────────────────────────────
  // Hydrates customer + subscription + invoice info from the
  // deployment server, which proxies to Stripe. NO Stripe SDK in
  // the browser — every API call to Stripe goes through our
  // server (the intermediary pattern Scott called out earlier).
  interface BillingStatus {
    readonly customerId: string;
    readonly email: string;
    readonly subscription: {
      readonly id: string;
      readonly status: string;
      readonly currentPeriodStart: number | null;
      readonly currentPeriodEnd: number | null;
    } | null;
    readonly invoiceCount: number;
    readonly projectCount: number;
  }
  let billing = $state<BillingStatus | null>(null);
  let billingError = $state<string | null>(null);
  let portalLoading = $state(false);

  async function refreshBilling(): Promise<void> {
    try {
      billing = (await node.dispatch("billing.status", {})) as BillingStatus;
      billingError = null;
    } catch (e) {
      billing = null;
      billingError = e instanceof Error ? e.message : String(e);
    }
  }
  $effect(() => {
    if (identity.user) void refreshBilling();
  });

  async function openPortal(): Promise<void> {
    portalLoading = true;
    try {
      const result = (await node.dispatch("billing.openPortal", {
        returnUrl: window.location.href,
      })) as { url: string };
      window.location.href = result.url;
    } catch (e) {
      billingError = e instanceof Error ? e.message : String(e);
    } finally {
      portalLoading = false;
    }
  }

  // ── D9.2 — manual usage sweep ──────────────────────────────
  // Drives `billing.sweepUsage` which walks every metric for every
  // project the user owns, computes the delta vs. the last sample,
  // and posts a Stripe usage record. The cron schedule does the
  // same thing every 365 days (placeholder cadence — Scott drops
  // this to a real interval at external launch). The button gives
  // operators an on-demand trigger without waiting for the cron.
  interface MetricSweepRow {
    readonly metric: string;
    readonly priorValue: number | null;
    readonly currentValue: number | null;
    readonly delta: number | null;
    readonly usageRecordId: string | null;
    readonly skipped: boolean;
    readonly reason: string | null;
  }
  interface SweepResult {
    readonly userId: string;
    readonly durationMs: number;
    readonly totals: {
      readonly users: number;
      readonly projects: number;
      readonly metricsTried: number;
      readonly usageRecordsPosted: number;
      readonly errors: number;
    };
    readonly projects: ReadonlyArray<{
      readonly projectId: string;
      readonly metrics: readonly MetricSweepRow[];
    }>;
  }
  let sweepRunning = $state(false);
  let sweepResult = $state<SweepResult | null>(null);
  let sweepError = $state<string | null>(null);
  async function runSweep(): Promise<void> {
    sweepRunning = true;
    sweepError = null;
    try {
      const result = (await node.dispatch("billing.sweepUsage", {})) as SweepResult;
      sweepResult = result;
      // Refresh status — sweep may have created the customer's
      // subscription on first call, etc.
      await refreshBilling();
    } catch (e) {
      sweepError = e instanceof Error ? e.message : String(e);
    } finally {
      sweepRunning = false;
    }
  }

  // ── Plan tier (mocked) ─────────────────────────────────────
  // Real plan tier comes from a billing adapter once it ships.
  // For now everyone's on "developer" (free) with reasonable caps.
  const PLAN = {
    name: "developer",
    priceUsdMonth: 0,
    perks: [
      "1 GB storage",
      "100 GB bandwidth",
      "500,000 db rows",
      "100 compute hrs",
      "unlimited projects",
    ],
    quotas: {
      computeHrs: 100,
      bandwidthGb: 100,
      storageGb: 1,
      rows: 500_000,
    },
  };

  // ── Usage (aggregated across projects — 0 today) ──────────
  // Real usage arrives via TelemetryIO once connectors land. The
  // shape of `usage` mirrors the plan caps so QuotaMeter consumes
  // them cleanly.
  const usage = {
    computeHrs: 0,
    bandwidthGb: 0,
    storageGb: 0,
    rows: 0,
  };

  // ── Recent gate verdicts (filtered) ───────────────────────
  const gateHistory = $derived(
    timeline.events.filter((e) =>
      e.op.startsWith("gate.") ||
      [
        "gatedDeploy",
        "approve",
        "deny",
      ].includes(e.op),
    ),
  );

  function timeAgo(ms: number): string {
    const diff = Date.now() - ms;
    if (diff < 60_000) return "just now";
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
    return `${Math.floor(diff / 86_400_000)}d ago`;
  }
</script>

{#if !identity.user}
  <div class="notfound">
    <h1>Not signed in</h1>
    <p>Go back to <a href="#/">sign in</a>.</p>
  </div>
{:else}
  <div class="screen">
    <header class="hero">
      <h1>Billing</h1>
      <p class="meta">
        <code>{identity.user.email}</code>
        · {identity.projects.length} project{identity.projects.length === 1 ? "" : "s"}
        on the <strong>{PLAN.name}</strong> plan
      </p>
    </header>

    {#if !io.bundle.deployment.effecting}
      <FaceLedgerNotice
        method="dispatch(gates.*) + billing adapter"
        phase="connector-wiring + billing-adapter-build"
        detail="control plane mounted; command port not wired AND billing adapter not built — quotas show 0, verdicts show only refusals, real plan + invoices arrive with the billing adapter"
      />
    {/if}

    {#if billingError}
      <FaceLedgerNotice
        method="dispatch(billing.status)"
        phase="D9"
        detail={billingError}
      />
    {/if}

    {#if gates.lastError}
      <FaceLedgerNotice
        method="dispatch(gates.gatedDeploy)"
        phase="connector-wiring"
        detail={gates.lastError.message}
      />
    {/if}

    <!-- ── Stripe billing account (D9) ───────────────────────── -->
    <section class="rail plan-card">
      <header>
        <h2>Billing account</h2>
        {#if billing}
          <StatusPill
            origin="ok"
            stage={billing.subscription ? billing.subscription.status : "no subscription"}
            compact
          />
        {/if}
      </header>
      {#if billing}
        <dl class="account-meta">
          <div><dt>Stripe customer</dt><dd><code>{billing.customerId}</code></dd></div>
          <div><dt>email</dt><dd><code>{billing.email}</code></dd></div>
          <div><dt>projects</dt><dd>{billing.projectCount}</dd></div>
          <div><dt>invoices</dt><dd>{billing.invoiceCount}</dd></div>
          {#if billing.subscription}
            <div><dt>subscription</dt><dd><code>{billing.subscription.id}</code> · {billing.subscription.status}</dd></div>
          {:else}
            <div>
              <dt>subscription</dt>
              <dd>
                <em>none</em> — free tier. Paid plans land in D9.1 once
                pricing is configured in Stripe.
              </dd>
            </div>
          {/if}
        </dl>
        <div class="plan-actions">
          <button type="button" onclick={() => void openPortal()} disabled={portalLoading}>
            {portalLoading ? "opening…" : "Manage in Stripe portal"}
          </button>
        </div>
      {:else}
        <p class="rail-help">
          {billingError ? "billing fetch failed — see error above" : "loading billing account…"}
        </p>
      {/if}
    </section>

    <!-- ── Usage sweep manual trigger ─────────────────────────── -->
    <section class="rail">
      <header><h2>Usage sweep</h2></header>
      <p class="rail-help">
        Click to push the latest usage numbers to your billing account.
        Normally this runs automatically — the button is here for
        manual reconciliation if you've just made a big change and
        want it reflected immediately.
      </p>
      <div class="plan-actions">
        <button type="button" onclick={() => void runSweep()} disabled={sweepRunning}>
          {sweepRunning ? "sweeping…" : "Run usage sweep now"}
        </button>
      </div>
      {#if sweepError}
        <p class="rail-help" style="color: var(--c-error, #c44)">
          sweep failed: <code>{sweepError}</code>
        </p>
      {/if}
      {#if sweepResult}
        <dl class="account-grid">
          <div><dt>duration</dt><dd>{sweepResult.durationMs} ms</dd></div>
          <div><dt>projects swept</dt><dd>{sweepResult.totals.projects}</dd></div>
          <div><dt>metrics tried</dt><dd>{sweepResult.totals.metricsTried}</dd></div>
          <div><dt>usage records posted</dt><dd>{sweepResult.totals.usageRecordsPosted}</dd></div>
          <div><dt>errors</dt><dd>{sweepResult.totals.errors}</dd></div>
        </dl>
        {#if sweepResult.projects.length > 0}
          <h3 style="margin-top: 1rem">Per-project breakdown</h3>
          {#each sweepResult.projects as p (p.projectId)}
            <details>
              <summary><code>{p.projectId}</code></summary>
              <ol class="event-list">
                {#each p.metrics as m, i (`${p.projectId}:${m.metric}:${i}`)}
                  <li class="event-row">
                    <code>{m.metric}</code>
                    <span class="event-code">
                      {#if m.usageRecordId}
                        <code>+{m.delta?.toFixed(2) ?? "?"}</code> → <code>{m.usageRecordId}</code>
                      {:else if m.skipped}
                        skipped: {m.reason ?? "(no reason)"}
                      {:else}
                        Δ {m.delta?.toFixed(2) ?? "?"}
                      {/if}
                    </span>
                  </li>
                {/each}
              </ol>
            </details>
          {/each}
        {/if}
      {/if}
    </section>

    <!-- ── Current plan (legacy mock card) ───────────────────── -->
    <section class="rail plan-card">
      <header><h2>Plan tier</h2></header>
      <div class="plan-grid">
        <div class="plan-main">
          <span class="plan-name">{PLAN.name}</span>
          <span class="plan-price">
            ${PLAN.priceUsdMonth.toFixed(2)} <span class="plan-period">/ month</span>
          </span>
        </div>
        <ul class="perks">
          {#each PLAN.perks as p (p)}
            <li>{p}</li>
          {/each}
        </ul>
        <div class="plan-actions">
          <button type="button" disabled title="Paid tiers ship at external launch">Upgrade plan</button>
        </div>
      </div>
    </section>

    <!-- ── Usage + quotas ────────────────────────────────────── -->
    <section class="rail">
      <header>
        <h2>Usage this month</h2>
        <span class="cycle">cycle: {new Date().toLocaleDateString(undefined, { month: "long", year: "numeric" })}</span>
      </header>
      <div class="meters">
        <QuotaMeter label="compute"   used={usage.computeHrs}  limit={PLAN.quotas.computeHrs} unit="hrs" />
        <QuotaMeter label="bandwidth" used={usage.bandwidthGb} limit={PLAN.quotas.bandwidthGb} unit="GB" />
        <QuotaMeter label="storage"   used={usage.storageGb}   limit={PLAN.quotas.storageGb}   unit="GB" />
        <QuotaMeter label="rows"      used={usage.rows}        limit={PLAN.quotas.rows} />
      </div>
      <p class="caveat">
        Aggregated across all your projects. Counters tick as your
        apps run — they refresh roughly every minute.
      </p>
    </section>

    <!-- ── Gate dimensions (u2) ──────────────────────────────── -->
    <section class="rail">
      <header>
        <h2>Gate dimensions</h2>
        <StatusPill origin="ok" stage="u2 gate tier" compact />
      </header>
      <p class="rail-help">
        Every deploy passes through these five gates. Each verdict
        is recorded in your activity timeline so you can see exactly
        why a deploy ran (or didn't).
      </p>
      <ul class="dim-list">
        {#each gates.dimensions as d (d)}
          <li class="dim-row">
            <code>{d}</code>
            <span class="dim-meta">
              {#if d === "budget"}ENFORCED — overruns block deploys{/if}
              {#if d === "freeze"}change-freeze windows{/if}
              {#if d === "health"}service health pre-check{/if}
              {#if d === "attestation"}supply-chain provenance{/if}
              {#if d === "approval"}human-in-the-loop sign-off{/if}
            </span>
          </li>
        {/each}
      </ul>
    </section>

    <!-- ── Recent verdicts ───────────────────────────────────── -->
    <section class="rail">
      <header><h2>Recent gate verdicts</h2></header>
      {#if gateHistory.length === 0}
        <p class="empty">no gate verdicts yet · run a deploy from the Deployments tab</p>
      {:else}
        <ol class="event-list">
          {#each gateHistory.slice(-8).reverse() as e, i (e.atMs + ":" + e.op + ":" + (e.code ?? "") + ":" + i)}
            <li class="event-row">
              <StatusPill origin={e.origin} stage={e.op.replace(/^gate\./, "")} compact />
              <span class="event-code"><code>{e.code}</code></span>
              <span class="event-time">{timeAgo(e.atMs)}</span>
            </li>
          {/each}
        </ol>
      {/if}
    </section>

    <!-- ── Invoices placeholder ──────────────────────────────── -->
    <section class="rail">
      <header><h2>Invoices</h2></header>
      <p class="empty">
        Invoices + payment method + billing email arrive with the
        billing adapter. Free-tier accounts won't have invoices to
        show anyway — when you upgrade, this rail lights up.
      </p>
    </section>
  </div>
{/if}

<style>
  /* D9 billing account block */
  .account-meta {
    margin: 0;
    display: grid;
    grid-template-columns: 160px 1fr;
    gap: var(--space-2) var(--space-3);
  }
  .account-meta > div { display: contents; }
  .account-meta dt {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--text-tertiary);
  }
  .account-meta dd { margin: 0; font-size: 12px; }
  .account-meta dd code {
    font-family: var(--type-mono);
    font-size: 11px;
    color: var(--text-secondary);
    word-break: break-all;
  }
  .account-meta em { color: var(--text-tertiary); font-style: italic; }

  .screen {
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
    font-family: var(--type-font);
    color: var(--text-primary);
  }
  .hero {
    padding-bottom: var(--space-4);
    border-bottom: 0.5px solid var(--border-subtle);
    display: flex;
    flex-direction: column;
    gap: var(--space-1, 4px);
  }
  h1 { margin: 0; font-size: 22px; font-weight: 600; }
  .meta { margin: 0; font-size: 12px; color: var(--text-tertiary); }
  .meta code   { font-family: var(--type-mono); font-size: 11px; color: var(--text-secondary); }
  .meta strong { color: var(--text-primary); font-weight: 500; }

  .rail {
    padding: var(--space-4);
    background: var(--glass-fill);
    border: 0.5px solid var(--border);
    border-radius: var(--radius-md);
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  .rail header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: var(--space-3);
  }
  h2 {
    margin: 0;
    font-size: 12px;
    font-weight: 600;
    color: var(--text-tertiary);
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }
  .cycle {
    font-size: 11px;
    color: var(--text-tertiary);
    font-family: var(--type-mono);
  }
  .rail-help, .caveat {
    margin: 0;
    font-size: 12px;
    color: var(--text-secondary);
    line-height: 1.55;
  }
  /* Plan card */
  .plan-card .plan-grid {
    display: grid;
    grid-template-columns: 1fr 2fr auto;
    gap: var(--space-5);
    align-items: center;
  }
  .plan-main {
    display: flex;
    flex-direction: column;
    gap: var(--space-1, 4px);
  }
  .plan-name {
    font-size: 22px;
    font-weight: 600;
    color: var(--text-primary);
    text-transform: capitalize;
  }
  .plan-price {
    font-size: 16px;
    font-weight: 500;
    color: var(--text-primary);
    font-family: var(--type-mono);
  }
  .plan-period {
    font-size: 12px;
    color: var(--text-tertiary);
    font-weight: 400;
  }
  .perks {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-1, 4px);
  }
  .perks li {
    font-size: 12px;
    color: var(--text-secondary);
    padding-left: var(--space-3);
    position: relative;
  }
  .perks li::before {
    content: "✓";
    position: absolute;
    left: 0;
    color: var(--success);
  }
  .plan-actions button {
    appearance: none;
    background: var(--glass-fill);
    border: 0.5px solid var(--border);
    border-radius: var(--radius-sm);
    color: var(--text-tertiary);
    padding: var(--space-2) var(--space-3);
    font: inherit;
    font-size: 11px;
    cursor: not-allowed;
    opacity: 0.5;
  }

  /* Meters */
  .meters {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  /* Dimensions */
  .dim-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .dim-row {
    display: grid;
    grid-template-columns: 140px 1fr;
    gap: var(--space-3);
    align-items: baseline;
    padding: var(--space-2) var(--space-3);
    border-bottom: 0.5px solid var(--border-subtle);
    font-size: 12px;
  }
  .dim-row code {
    font-family: var(--type-mono);
    font-size: 12px;
    color: var(--text-primary);
  }
  .dim-meta {
    font-size: 11px;
    color: var(--text-secondary);
  }

  /* Events */
  .empty {
    margin: 0;
    padding: var(--space-3);
    font-size: 12px;
    color: var(--text-tertiary);
    font-style: italic;
  }
  .event-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-1, 4px);
  }
  .event-row {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-2);
    font-size: 12px;
  }
  .event-code { flex: 1; }
  .event-code code {
    font-family: var(--type-mono);
    font-size: 11px;
    color: var(--text-secondary);
  }
  .event-time { font-size: 11px; color: var(--text-tertiary); }

  /* Not found */
  .notfound {
    max-width: 480px;
    margin: var(--space-8) auto;
    padding: var(--space-6);
    background: var(--glass-fill);
    border: 0.5px solid var(--border);
    border-radius: var(--radius-lg);
    text-align: center;
    font-family: var(--type-font);
  }
  .notfound h1 { margin: 0 0 var(--space-3); font-size: 18px; font-weight: 600; }
  .notfound p  { margin: 0; font-size: 13px; color: var(--text-secondary); line-height: 1.55; }
  .notfound a  { color: var(--accent-text); }

  @media (max-width: 800px) {
    .plan-card .plan-grid { grid-template-columns: 1fr; }
    .dim-row { grid-template-columns: 1fr; }
  }
</style>
